import { EDatasourceType, ESdChangeReason } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance, RuleObject } from "antd/es/form";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { showDomainAccessRequiredModal } from "../../../store/domain-access-modal/actions";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { CREATE_DATASOURCE_STATUS_LABELS } from "./connector-picker-popover";
import { CSHARP_TEMPLATE_OPTIONS, GATED_CSHARP_TEMPLATE_ACCESS, GATED_CSHARP_TEMPLATES } from "./csharp-templates";
import { createDatasource, CreateDatasourceOutcome } from "./datasource-create-actions";
import { getDatasourceNameFormatError } from "./datasource-name-validation";
import { DatasourceCreateCategory, getConnectorsFor, IDatasourceConnector } from "./datasources-landing-model";

/**
 * "Which connector?" as a sheet, replacing the three-column screen that used to be the whole
 * centre of the tool. One card opens it on one category; the left tree's "+" opens it on all
 * of them.
 *
 * Picking a type reveals the name field in place rather than moving to another screen — the
 * type and its name are one decision, and the per-type connection panel that opens afterwards
 * is where the real work starts.
 */

export interface IConnectorPickerModalProps {
  visible: boolean;
  category: DatasourceCreateCategory;
  onClose: () => void;
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
}

interface IConnectorPickerModalState {
  selectedType?: EDatasourceType;
  selectedTemplate: EDatasourceType;
  /** Where CREATE stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

interface IConnectorFormValues {
  name: string;
}

class ConnectorPickerModal extends PureSdComponent<
  IConnectorPickerModalProps & IWithSDProps,
  IConnectorPickerModalState
> {
  formRef = React.createRef<FormInstance>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IConnectorPickerModalProps & IWithSDProps) {
    super(props);
    this.state = { selectedType: undefined, selectedTemplate: EDatasourceType.CSharpFunctions, saveStatus: "idle" };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate(prevProps: IConnectorPickerModalProps & IWithSDProps): void {
    // A fresh sheet every time it opens: the previous pick and its name are not this decision.
    if (this.props.visible && !prevProps.visible) {
      this.setState({ selectedType: undefined, selectedTemplate: EDatasourceType.CSharpFunctions });
      this.formRef.current?.resetFields();
    }
  }

  getConnectors = (): IDatasourceConnector[] =>
    getConnectorsFor(this.props.category, this.props.sd?.project?.projectType);

  getSections = (): string[] => {
    const sections: string[] = [];
    this.getConnectors().forEach((connector: IDatasourceConnector): void => {
      if (!sections.includes(connector.section)) sections.push(connector.section);
    });
    return sections;
  };

  /** The gate is the old screen's: it never hides or disables an entry, it refuses the pick. */
  handleSelect = (connector: IDatasourceConnector): void => {
    if (connector.accessName && KMainFunctions.isDomainHasAccess(connector.accessName) === false) {
      this.props.showDomainAccessRequiredModal(connector.accessName);
      return;
    }
    this.setState({ selectedType: connector.type }, (): void => {
      if (connector.type === EDatasourceType.CSharpFunctions) {
        void this.handleTemplateChange(EDatasourceType.CSharpFunctions);
      }
    });
  };

  /**
   * Prefetching the template here is what lets the C# editor open already seeded. The final
   * refetch — with the name in it — happens on create; see `datasource-create-actions.ts`.
   */
  handleTemplateChange = async (value: EDatasourceType): Promise<void> => {
    if (GATED_CSHARP_TEMPLATES.includes(value)) {
      if (KMainFunctions.isDomainHasAccess(GATED_CSHARP_TEMPLATE_ACCESS) === false) {
        this.props.showDomainAccessRequiredModal(GATED_CSHARP_TEMPLATE_ACCESS);
        return;
      }
    }
    const response = await DatasourceService.GetCSharpCodeTemplates(value);
    if (response.status === 200) {
      if (this.state.selectedType === EDatasourceType.CSharpFunctions) {
        this.props.sd.setActiveCreatingCSharpCode(response.data, value);
      }
      this.setState({ selectedTemplate: value });
    }
  };

  handleFinish = async (values: IConnectorFormValues): Promise<void> => {
    const type: EDatasourceType | undefined = this.state.selectedType;
    if (type === undefined || !values.name) return;

    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // create a second datasource behind the closing sheet.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    const outcome: CreateDatasourceOutcome = await createDatasource({
      sd: this.props.sd,
      type,
      name: values.name,
      template: this.state.selectedTemplate,
      // Saved on the spot: the ✓ goes up first, then the sheet gives way to the new
      // datasource's editor — which is the result, so there is nothing for a toast to add.
      openCreated: (open: () => void): void => {
        this.saveStatus.succeed((): void => {
          open();
          this.props.onClose();
        });
      }
    });
    if (outcome === "rejected") {
      // The sheet stays open with what the user typed still in it; the button says it failed.
      this.saveStatus.fail();
    } else if (outcome === "connection-panel") {
      // Nothing was saved: the type's own panel opened and will do the saving.
      this.saveStatus.reset();
      this.props.onClose();
    }
  };

  handleSubmitClick = (): void => {
    this.formRef.current?.submit();
  };

  /** Format-only; the same rule `ConnectorPickerPopover` validates its own field against. */
  validateName = async (_rule: RuleObject, value: string): Promise<void> => {
    const error: string | undefined = getDatasourceNameFormatError(value);
    if (error) throw new Error(error);
  };

  renderConnector = (connector: IDatasourceConnector): React.ReactNode => {
    const isSelected: boolean = this.state.selectedType === connector.type;
    return (
      <div
        key={connector.type}
        className={`kuika-connector-picker__item${isSelected ? " kuika-connector-picker__item--selected" : ""}`}
        data-testid={`${connector.type}Selection`}
        onClick={(): void => this.handleSelect(connector)}
      >
        <span className="kuika-connector-picker__icon">
          <KuikaSvg type="datasource-icons" name={connector.iconName} width={24} height={24} />
        </span>
        <span className="kuika-connector-picker__text">
          <span
            className="kuika-connector-picker__name"
            data-testid={`${connector.label.toLowerCase().replace(/\s/g, "")}Selection`}
          >
            {connector.label}
            {connector.badges?.map((badge: string) => (
              <span key={badge} className="kuika-connector-picker__badge">
                {badge}
              </span>
            ))}
          </span>
          <span className="kuika-connector-picker__description">{connector.description}</span>
        </span>
      </div>
    );
  };

  renderSection = (section: string): React.ReactNode => {
    const connectors: IDatasourceConnector[] = this.getConnectors().filter(
      (connector: IDatasourceConnector): boolean => connector.section === section
    );
    if (connectors.length === 0) return null;
    return (
      <div className="kuika-connector-picker__section" key={section}>
        {/* The old screen's heading id: the E2E suite uses it as a marker that the picker is up. */}
        <span
          className="kuika-connector-picker__section-title"
          data-testid={section === "Databases" ? "databaseCategoryTitle" : undefined}
        >
          {section}
        </span>
        <div className="kuika-connector-picker__items">{connectors.map(this.renderConnector)}</div>
      </div>
    );
  };

  getTitle = (): string => {
    if (this.props.category === "database") return "Create a Database";
    if (this.props.category === "api") return "Create an API";
    if (this.props.category === "function") return "Create a Function";
    return "Add a data source";
  };

  render() {
    const isCSharp: boolean = this.state.selectedType === EDatasourceType.CSharpFunctions;

    return (
      <KModal
        showModal={this.props.visible}
        closeModal={this.props.onClose}
        centered
        width={560}
        isLoading={this.state.saveStatus === "loading"}
        className="kuika-connector-picker"
        header={this.getTitle()}
        footer={
          <KModalFooter
            onCancel={this.props.onClose}
            cancelTestId="cancelCreateDatasourceButton"
            submitLabel="Create"
            submitTestId="createDatasourceButton"
            onSubmit={this.handleSubmitClick}
            disabled={this.state.selectedType === undefined}
            status={this.state.saveStatus}
            statusLabels={CREATE_DATASOURCE_STATUS_LABELS}
          />
        }
      >
        <div className="kuika-connector-picker__body" data-testid="createDatasourcePanel">
          {this.getSections().map(this.renderSection)}

          {this.state.selectedType !== undefined && (
            <Form ref={this.formRef} onFinish={this.handleFinish} layout="vertical">
              <div className="kuika-connector-picker__form">
                <Form.Item name="name" label="Name" rules={[{ required: true, validator: this.validateName }]}>
                  <KuikaInput
                    data-testid="datasourceNameInput"
                    placeholder="Enter a name"
                    size="md"
                    autoFocus
                    // Chrome reads `id="name"` and this placeholder as a person's name and offers
                    // every datasource ever named here, painting its own light surface over the
                    // field as you move through the list.
                    autoComplete="off"
                  />
                </Form.Item>
                {isCSharp && (
                  <Form.Item label="Template">
                    <KuikaSelectbox
                      size="md"
                      placeholder="Select a Template"
                      value={this.state.selectedTemplate}
                      options={CSHARP_TEMPLATE_OPTIONS}
                      onChange={(value: EDatasourceType): void => void this.handleTemplateChange(value)}
                      data-testid="datasourceTemplateSelect"
                    />
                  </Form.Item>
                )}
              </div>
            </Form>
          )}
        </div>
      </KModal>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const connectorPickerModal = connect(
  null,
  mapDispatchToProps
)(withSD<IConnectorPickerModalProps>(ConnectorPickerModal));

export { connectorPickerModal as ConnectorPickerModal };
